import React, { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';

import { Button, Confirm, Input, InputType, Modal, SpinnerOverlay } from '@components/common';
import RadioButton from '@components/common/Form/RadioButton';

import { ITechnologyDTO } from '@interfaces/cells/dto/cellsDTO';
import { INokiaProfile, ISiteProfile } from '@interfaces/cells/siteProfile';
import { TTechnologyNameTitle } from '@interfaces/common/common';

import { ETechnologiesValues, TTechnologiesValues } from '@enums/common';
import { EValidationMode } from '@enums/validationMode';

import { modalCloseConfirm } from '@constants/modals';
import { technologyButtons } from '@components/routes/AdminPanel/SiteProfile/constants';

import { schema } from './schema';

import styles from './TechnologyForm.module.scss';

export type TTechnologyFormSubmitHandler = ({ id, data }: {
	id: string;
	data: ITechnologyDTO;
}) => void;

interface ITechnologyFormProps {
	isEditMode?: boolean;
	isLoadingForm?: boolean;
	setIsDirtyFormModal: React.Dispatch<React.SetStateAction<boolean>>;
	submitHandler: TTechnologyFormSubmitHandler;
	toggleTechnologyModal: () => void;
	siteProfile: INokiaProfile | ISiteProfile;
	existingTechnologies: TTechnologiesValues[] | undefined;
	editableTechnology: TTechnologyNameTitle & { node: string } | null;
}

const TechnologyForm = ({
	editableTechnology,
	existingTechnologies,
	isEditMode,
	isLoadingForm,
	setIsDirtyFormModal,
	siteProfile,
	submitHandler,
	toggleTechnologyModal,
}: ITechnologyFormProps) => {
	const [isConfirmModal, setIsConfirmModal] = useState(false);

	const notExistingTechnologyButtons = technologyButtons
		.filter(button => !existingTechnologies?.includes(button.value));

	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		setValue,
		watch,
	} = useForm<FieldValues>({
		mode: EValidationMode.all,
		resolver: yupResolver(schema),
		defaultValues: {
			nodeId: editableTechnology?.node || '',
			technology: editableTechnology?.name || notExistingTechnologyButtons[0]?.value,
		},
	});

	const is5G = watch('technology') === ETechnologiesValues.T_5G;

	useEffect(() => setIsDirtyFormModal(isDirty), [isDirty, setIsDirtyFormModal]);

	const handleCloseConfirmModal = () => setIsConfirmModal(false);

	const submitForm = (values: FieldValues) => {
		const node = is5G
			? { gnodeBId: values.nodeId }
			: { enodeBId: values.nodeId };

		const data = {
			technology: values.technology,
			...node,
		};

		submitHandler({ id: siteProfile.id, data });
	};

	const handleCloseModal = () => {
		if (isDirty) setIsConfirmModal(true);
		else toggleTechnologyModal();
	};

	return (
		<div className={styles.technologyFormContainer}>
			{isLoadingForm && <SpinnerOverlay />}
			<Modal
				isOpen={isConfirmModal}
				onClose={handleCloseConfirmModal}
			>
				<Confirm
					onClose={handleCloseConfirmModal}
					onSubmit={toggleTechnologyModal}
					{...modalCloseConfirm}
				/>
			</Modal>

			{!!notExistingTechnologyButtons.length && !isEditMode
				&& <>
					<div className={styles.technologiesTitle}>
						Select technology
					</div>
					<div className={styles.technologies}>
						{notExistingTechnologyButtons.map((technologyButton, index) => (
								<RadioButton
									key={technologyButton.value}
									defaultChecked={index === 0}
									id={technologyButton.value}
									label={technologyButton.label}
									name='technology'
									setValue={setValue}
								/>
							),
						)}
					</div>
				</>
			}
			<div>
				<Input
					errorMessage={errors?.nodeId?.message}
					label={is5G ? 'gNodeB ID' : 'eNodeB ID'}
					name='nodeId'
					placeholder={is5G ? 'Enter gNodeB ID' : 'Enter eNodeB ID'}
					register={register}
					required
					setValue={setValue}
					type={InputType.integer}
				/>
			</div>
			<div className={styles.buttons}>
				<Button
					onClick={handleCloseModal}
					text='Cancel'
					withoutBackground
				/>
				<Button
					onClick={handleSubmit(submitForm)}
					text={isEditMode ? 'Save' : 'Add'}
				/>
			</div>
		</div>
	);
};

export default TechnologyForm;
